import React, { createContext, useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import socket from '../socket';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    try {
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!localStorage.getItem('token'));

  const refreshUser = async () => {
    try {
      const response = await axios.get('/api/users/profile');
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setLoading(false); // Set loading to false after user data is refreshed
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // If refresh fails, clear token and user, and set loading to false
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (token) {
      refreshUser();
    } else {
      setLoading(false);
    }

    // Global real-time listeners
    socket.on('balance_updated', () => {
      console.log('Balance update received');
      refreshUser();
    });

    socket.on('transaction_updated', (data) => {
      console.log('Transaction update received');
      refreshUser();
    });

    socket.on('trade_updated', (data) => {
      console.log('Trade update received');
      refreshUser();
    });

    return () => {
      socket.off('balance_updated');
      socket.off('transaction_updated');
      socket.off('trade_updated');
      // Only emit leave_user if user exists
      if (user) {
        socket.emit('leave_user', user._id);
      }
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      socket.emit('join_user', user._id);
      toast.success('Login successful!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
      return false;
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await axios.post('/api/auth/register', {
        email,
        password,
        fullName
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      socket.emit('join_user', user._id);
      toast.success('Registration successful!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (user) {
      socket.emit('leave_user', user._id);
    }
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      updateUser,
      refreshUser,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};
